import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TabBar } from "../../components/TabBar";
import { SidebarNav } from "../../components/SidebarNav";
import { computeQuota } from "../../lib/quota";
import { isoDay, streakFromLogs } from "../../lib/streak";
import { useAppStore } from "../../state/app-store-context";
import { createDefaultHkdseDeck } from "../../lib/deck";
import { EditDeckModal } from "../deck/EditDeckModal";
import { WeeklyProgressWidget } from "./WeeklyProgressWidget";
import { LearningCalendarWidget } from "./LearningCalendarWidget";
import { MotivationalCardWidget } from "./MotivationalCardWidget";
import { HeaderScenicBackground, HeroMountainBackground } from "./ScenicMountainBackground";
import { QuickSearchModal } from "./QuickSearchModal";
import type { Deck, ExamId } from "../../lib/types";

const DSE_DEADLINE = "2027-04-08";
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const fmt = (n: number) => n.toLocaleString("en-US");

/** "8 Apr" */
function dateLabel(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

export function Today() {
  const { state, dispatch, ready } = useAppStore();
  const { settings, decks, progress, logs } = state;

  const [editingDeck, setEditingDeck] = useState<Deck | null>(null);
  const [showSearch, setShowSearch] = useState(false);
  const [showNotification, setShowNotification] = useState(false);

  useEffect(() => {
    if (ready && decks.length === 0) {
      dispatch({ type: "addDeck", deck: createDefaultHkdseDeck() });
      dispatch({ type: "setOnboarded", value: true });
    }
  }, [ready, decks.length, dispatch]);

  const now = new Date();
  const today = isoDay(now);
  const dateLine = `${WEEKDAYS[now.getDay()]} ${now.getDate()} ${MONTHS[now.getMonth()]}`;
  const dseDays = computeQuota({ total: 1, learned: 0, deadline: DSE_DEADLINE, today }).daysLeft;

  const streak = streakFromLogs(logs, today);
  const doneToday = logs.filter((l) => l.date.slice(0, 10) === today).reduce((s, l) => s + l.done, 0);
  const remaining = Math.max(0, settings.dailyGoal - doneToday);
  const donePct = settings.dailyGoal > 0 ? (doneToday / settings.dailyGoal) * 100 : 0;

  const nowMs = now.getTime();
  const due = Object.values(progress).filter((p) => new Date(p.nextDue).getTime() <= nowMs).length;

  const scheduled = decks.filter((d) => d.deadline);
  const unscheduled = decks.filter((d) => !d.deadline);

  const handleSaveDeck = (patch: {
    name: string;
    exam: ExamId;
    deadline?: string;
    deadlineLabel?: string;
    dailyNew: number;
  }) => {
    if (!editingDeck) return;
    dispatch({ type: "updateDeck", deckId: editingDeck.id, patch });
  };

  const handleDeleteDeck = (deckId: string) => {
    dispatch({ type: "removeDeck", deckId });
  };

  const learnerName = settings.learnerName?.trim() || "Kelvin";

  return (
    <div className="min-h-screen bg-[#F8FAFC] w-full overflow-x-hidden flex selection:bg-blue-100 selection:text-blue-900">
      {/* Left Sidebar Navigation - flush to the left edge of viewport */}
      <SidebarNav className="hidden md:flex sticky top-0 h-screen w-[220px] lg:w-[240px] shrink-0" />

      {/* Center Main Dashboard Content - takes full fluid width edge-to-edge */}
      <main className="flex-1 min-w-0 w-full px-4 sm:px-6 md:px-8 xl:px-10 py-5 sm:py-6 relative z-10 flex flex-col justify-start overflow-x-hidden pb-24 md:pb-12 xl:pb-8">
        <div className="w-full min-w-0 overflow-x-hidden">
          {/* Scenic background mountains & sunrise */}
          <HeaderScenicBackground />

          {/* Top Header Bar */}
          <div className="flex items-center justify-between relative z-10 pt-1 pb-5 sm:pb-6 gap-2 w-full min-w-0">
            <div className="min-w-0 flex-1">
              <h1 className="text-[22px] sm:text-[26px] md:text-[28px] font-bold text-slate-900 tracking-tight flex items-center gap-1.5 sm:gap-2 m-0 truncate">
                <span>早安，{learnerName}</span>
                <span className="inline-block text-[22px] sm:text-[24px]">👋</span>
              </h1>
              <p className="font-grotesk text-[12px] sm:text-[13px] md:text-[14px] text-slate-500 mt-0.5 sm:mt-1 m-0 truncate">
                {dateLine} · 距 DSE 還有 {dseDays} 天
              </p>
            </div>

            {/* Top Right Controls */}
            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
              {/* Search button */}
              <button
                type="button"
                onClick={() => setShowSearch(true)}
                aria-label="搜尋單詞"
                className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 rounded-full bg-white/90 hover:bg-white text-slate-600 hover:text-slate-900 border border-slate-200/70 shadow-2xs flex items-center justify-center transition-all cursor-pointer hover:scale-105 active:scale-95"
              >
                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </button>

              {/* Notifications Bell with unread dot */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowNotification(!showNotification)}
                  aria-label="查看通知"
                  className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 rounded-full bg-white/90 hover:bg-white text-slate-600 hover:text-slate-900 border border-slate-200/70 shadow-2xs flex items-center justify-center transition-all cursor-pointer hover:scale-105 active:scale-95 relative"
                >
                  <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                  {/* Red badge dot */}
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-rose-500 ring-2 ring-white" />
                </button>

                {/* Notification Popover */}
                {showNotification && (
                  <div className="absolute right-0 mt-2 w-64 p-3.5 bg-white rounded-2xl shadow-xl border border-slate-100 z-50 text-[12.5px] text-slate-700 animate-pop-spring">
                    <div className="font-bold text-slate-900 mb-1 flex items-center gap-1.5">
                      <span>🔔</span>
                      <span>今日學習提醒</span>
                    </div>
                    <div className="text-slate-600 leading-relaxed">
                      今天還有 <strong className="text-blue-600">{remaining}</strong> 個新詞目標未完成，堅持練習讓單詞成為你的力量！
                    </div>
                  </div>
                )}
              </div>

              {/* User Avatar dropdown */}
              <Link
                to="/settings"
                className="flex items-center gap-1.5 p-1 sm:pl-1 sm:pr-2.5 md:pr-3 rounded-full bg-white/90 hover:bg-white border border-slate-200/70 shadow-2xs transition-all no-underline text-slate-700 hover:text-slate-900 hover:scale-102"
              >
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-slate-200 text-slate-700 font-bold text-xs flex items-center justify-center shrink-0">
                  {learnerName.charAt(0).toUpperCase()}
                </div>
                <span className="text-[13px] font-semibold text-slate-800 hidden sm:inline">
                  {learnerName}
                </span>
                <svg className="w-3.5 h-3.5 text-slate-400 hidden sm:inline" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </Link>
            </div>
          </div>

          {/* Scenic Midnight Mountain Hero Banner ("今日任務 Today's quota") */}
          <div className="relative rounded-[20px] sm:rounded-[24px] overflow-hidden shadow-xl border border-slate-800/60 text-white p-5 sm:p-7 md:p-8 bg-[#111A2E] z-10 mb-6 sm:mb-8 w-full min-w-0">
            {/* Mountain Silhouettes Graphic Background */}
            <HeroMountainBackground />

            {/* Hero Banner Header */}
            <div className="relative z-10 flex items-start justify-between gap-2 min-w-0">
              <div className="text-[12px] sm:text-[13.5px] text-slate-300 font-grotesk tracking-wide font-medium truncate">
                今日任務 Today's quota
              </div>

              {/* Handwritten Chinese quote in top-right */}
              <div className="text-right font-['Ma_Shan_Zheng','Caveat',cursive] text-[13px] sm:text-[15px] md:text-[16px] text-slate-200/95 leading-tight select-none shrink-0">
                持續累積，<br />
                讓單詞成為你的力量。
              </div>
            </div>

            {/* Progress Ring & Numbers */}
            <div className="relative z-10 flex items-center gap-4 sm:gap-6 mt-4 mb-4 sm:mb-5 min-w-0">
              {/* Circular Progress Gauge */}
              <div className="relative w-[64px] h-[64px] sm:w-[76px] sm:h-[76px] shrink-0 flex items-center justify-center">
                <svg className="w-full h-full -rotate-90 transform" viewBox="0 0 76 76">
                  <circle
                    cx="38"
                    cy="38"
                    r="31"
                    stroke="rgba(255, 255, 255, 0.15)"
                    strokeWidth="6"
                    fill="none"
                  />
                  <circle
                    cx="38"
                    cy="38"
                    r="31"
                    stroke="#38BDF8"
                    strokeWidth="6"
                    strokeDasharray={2 * Math.PI * 31}
                    strokeDashoffset={2 * Math.PI * 31 * (1 - Math.min(1, donePct / 100))}
                    strokeLinecap="round"
                    fill="none"
                    className="transition-all duration-700 ease-out"
                  />
                </svg>
                <span className="absolute font-grotesk font-bold text-[14px] sm:text-[16px] text-white">
                  {Math.round(donePct)}%
                </span>
              </div>

              {/* Quota numbers */}
              <div className="flex flex-col min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="font-grotesk font-bold text-[36px] sm:text-[46px] text-white leading-none tracking-tight">
                    {doneToday}
                  </span>
                  <span className="font-grotesk text-[18px] sm:text-[22px] text-slate-400 font-normal">
                    / {settings.dailyGoal}
                  </span>
                </div>
                <div className="font-grotesk text-[12px] sm:text-[13px] text-slate-300 mt-1 truncate">
                  {remaining === 0 ? "🎉 今日目標已完成！" : `${remaining} 詞未完成`}
                </div>
              </div>
            </div>

            {/* Full width divider track */}
            <div className="relative z-10 w-full h-[1px] bg-white/10 my-4 sm:my-5" />

            {/* Bottom Row: 3 Metric Badges + 2 Action Buttons */}
            <div className="relative z-10 flex flex-col xl:flex-row xl:items-center justify-between gap-4 mt-1 min-w-0">
              {/* 3 Metric Badges */}
              <div className="grid grid-cols-3 gap-2 sm:gap-5 md:gap-8 min-w-0">
                {/* 待複習 Due */}
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <div className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 rounded-full bg-white/10 flex items-center justify-center text-sky-300 shrink-0">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                  </div>
                  <div className="flex flex-col leading-tight min-w-0">
                    <span className="font-grotesk font-bold text-[15px] sm:text-[18px] text-white leading-tight">
                      {due}
                    </span>
                    <span className="text-[10px] sm:text-[11.5px] text-slate-300 truncate">
                      待複習 <span className="font-grotesk text-slate-400 hidden sm:inline">Due</span>
                    </span>
                  </div>
                </div>

                {/* 新詞目標 New words */}
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <div className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 rounded-full bg-white/10 flex items-center justify-center text-sky-300 shrink-0">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                    </svg>
                  </div>
                  <div className="flex flex-col leading-tight min-w-0">
                    <span className="font-grotesk font-bold text-[15px] sm:text-[18px] text-white leading-tight">
                      {settings.dailyGoal}
                    </span>
                    <span className="text-[10px] sm:text-[11.5px] text-slate-300 truncate">
                      新詞目標 <span className="font-grotesk text-slate-400 hidden sm:inline">New words</span>
                    </span>
                  </div>
                </div>

                {/* 連續學習 Streak */}
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <div className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 rounded-full bg-white/10 flex items-center justify-center text-amber-400 shrink-0">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
                    </svg>
                  </div>
                  <div className="flex flex-col leading-tight min-w-0">
                    <span className="font-grotesk font-bold text-[15px] sm:text-[18px] text-white leading-tight">
                      {streak} 天
                    </span>
                    <span className="text-[10px] sm:text-[11.5px] text-slate-300 truncate">
                      連續學習 <span className="font-grotesk text-slate-400 hidden sm:inline">Streak</span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-2.5 sm:gap-3 xl:flex xl:items-center shrink-0 mt-1 xl:mt-0 min-w-0">
                {/* 開始複習 (0 due) */}
                <Link
                  to="/quiz/review"
                  className="px-3 sm:px-5 py-2.5 sm:py-3 rounded-xl sm:rounded-2xl bg-white hover:bg-slate-50 text-slate-900 shadow-sm active:scale-[0.98] transition-all flex items-center justify-center gap-2 no-underline cursor-pointer min-w-0"
                >
                  <div className="w-5 h-5 rounded-full flex items-center justify-center text-[#2563EB] shrink-0">
                    <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                      <polygon points="5 3 19 12 5 21 5 3" />
                    </svg>
                  </div>
                  <div className="flex flex-col text-left leading-tight min-w-0 truncate">
                    <span className="text-[13px] sm:text-[14.5px] font-bold text-slate-900 truncate">開始複習</span>
                    <span className="font-grotesk text-[10.5px] sm:text-[11.5px] text-slate-500 font-medium truncate">{due} due</span>
                  </div>
                </Link>

                {/* 學新詞 (20 new ›) */}
                <Link
                  to="/quiz/new"
                  className="px-3 sm:px-5 py-2.5 sm:py-3 rounded-xl sm:rounded-2xl bg-[#2563EB] hover:bg-blue-700 text-white shadow-md shadow-blue-500/25 active:scale-[0.98] transition-all flex items-center justify-center gap-2 no-underline cursor-pointer min-w-0"
                >
                  <div className="w-5 h-5 rounded-full flex items-center justify-center text-white shrink-0">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                    </svg>
                  </div>
                  <div className="flex flex-col text-left leading-tight min-w-0 truncate">
                    <span className="text-[13px] sm:text-[14.5px] font-bold text-white truncate">學新詞</span>
                    <span className="font-grotesk text-[10.5px] sm:text-[11.5px] text-blue-100 font-medium truncate">{settings.dailyGoal} new</span>
                  </div>
                  <span className="text-white/80 font-bold text-sm ml-0.5 hidden xs:inline">›</span>
                </Link>
              </div>
            </div>
          </div>

          {/* 我的詞庫 Decks Section */}
          <div className="mb-6 sm:mb-8 min-w-0 w-full">
            <div className="flex items-center justify-between mb-3.5 sm:mb-4 gap-2">
              <div className="flex items-baseline gap-1.5 sm:gap-2 min-w-0">
                <h2 className="text-[16px] sm:text-[18px] font-bold text-slate-900 m-0 truncate">我的詞庫</h2>
                <span className="font-grotesk text-[13px] sm:text-[15px] text-slate-400 font-normal truncate">Decks</span>
              </div>
              <Link
                to="/deck/new"
                className="inline-flex items-center gap-1 sm:gap-1.5 px-3 sm:px-4 py-1.5 rounded-xl bg-white hover:bg-blue-50/50 text-[#2563EB] border border-blue-200/80 shadow-2xs font-semibold text-[12px] sm:text-[13px] no-underline transition-all shrink-0"
              >
                <span className="font-bold text-[13px] sm:text-[14px] leading-none">+</span>
                <span>新建詞庫</span>
              </Link>
            </div>

            {/* Scheduled Decks */}
            <div className="space-y-3 sm:space-y-3.5 w-full min-w-0">
              {scheduled.map((deck) => (
                <ScheduledDeckCard
                  key={deck.id}
                  deck={deck}
                  progress={progress}
                  today={today}
                  onEdit={(d) => setEditingDeck(d)}
                />
              ))}

              {/* Unscheduled Decks */}
              {unscheduled.map((deck) => (
                <div
                  key={deck.id}
                  className="p-4 sm:p-5 rounded-2xl bg-white border border-dashed border-slate-200 hover:border-slate-400 transition-all flex items-center justify-between min-w-0 w-full"
                >
                  <Link to={`/deck/${deck.id}`} className="min-w-0 flex-1 no-underline">
                    <div className="text-[14px] sm:text-[15.5px] font-medium text-slate-900 flex items-center gap-1.5 truncate">
                      <span>{deck.name}</span>
                      <span className="text-slate-400">›</span>
                    </div>
                    <div className="font-grotesk text-[11.5px] sm:text-[12.5px] text-slate-500 mt-0.5 truncate">
                      {deck.wordIds.length} 詞待整理 · 未排程
                    </div>
                  </Link>
                  <button
                    type="button"
                    onClick={() => setEditingDeck(deck)}
                    className="text-[12px] font-medium px-3 py-1.5 rounded-xl border border-slate-200 bg-white hover:border-slate-400 text-slate-600 hover:text-slate-900 transition-colors cursor-pointer shrink-0 ml-2 shadow-2xs"
                    title="編輯詞庫設定"
                  >
                    編輯
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Widgets Section on screens smaller than xl (< 1280px) */}
          <div className="xl:hidden grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-4 pb-6 border-t border-slate-100 w-full min-w-0">
            <WeeklyProgressWidget logs={logs} />
            <LearningCalendarWidget logs={logs} />
            <div className="md:col-span-2 lg:col-span-1">
              <MotivationalCardWidget />
            </div>
          </div>
        </div>
      </main>

      {/* Right Sidebar Widgets Panel - pinned flush to the right edge of viewport */}
      <aside className="hidden xl:block w-[320px] 2xl:w-[350px] shrink-0 p-6 space-y-5 sticky top-0 h-screen overflow-y-auto border-l border-slate-100/90 bg-white/40 backdrop-blur-xs">
        {/* Top Handwritten Quote */}
        <div className="font-['Ma_Shan_Zheng','Caveat',cursive] text-[14px] text-slate-500/90 leading-normal select-none px-1">
          * 每天一點進步，<br />
          &nbsp;&nbsp;成就更好的自己。
        </div>

        <WeeklyProgressWidget logs={logs} />
        <LearningCalendarWidget logs={logs} />
        <MotivationalCardWidget />
      </aside>

      {/* Edit Deck Modal */}
      <EditDeckModal
        deck={editingDeck}
        isOpen={Boolean(editingDeck)}
        onClose={() => setEditingDeck(null)}
        onSave={handleSaveDeck}
        onDelete={handleDeleteDeck}
        learnedCount={editingDeck ? editingDeck.wordIds.filter((id) => progress[id]?.learnedAt).length : 0}
      />

      {/* Quick Search Modal */}
      <QuickSearchModal
        isOpen={showSearch}
        onClose={() => setShowSearch(false)}
      />

      {/* Mobile TabBar - only visible on < md */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40">
        <TabBar />
      </div>
    </div>
  );
}

function ScheduledDeckCard({
  deck,
  progress,
  today,
  onEdit,
}: {
  deck: Deck;
  progress: Record<string, { learnedAt?: string }>;
  today: string;
  onEdit: (deck: Deck) => void;
}) {
  const total = deck.wordIds.length;
  const learned = deck.wordIds.filter((id) => progress[id]?.learnedAt).length;
  const quota = computeQuota({ total, learned, deadline: deck.deadline!, today });
  const pct = total > 0 ? (learned / total) * 100 : 0;
  const displayName = deck.name.includes("詞") ? deck.name : `${deck.name} · ${fmt(total)} 詞`;

  return (
    <div className="rounded-2xl bg-white border border-slate-100/90 p-4 sm:p-5 shadow-xs hover:shadow-md transition-all flex items-center gap-3.5 sm:gap-4 group min-w-0 w-full overflow-hidden">
      {/* Peach/coral squircle open book icon container */}
      <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-[#FFF1EB] flex items-center justify-center text-[#F97316] shrink-0">
        <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
          <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
        </svg>
      </div>

      {/* Main deck details */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-1.5">
          <Link
            to={`/deck/${deck.id}`}
            className="text-[14.5px] sm:text-[16px] font-bold text-slate-900 group-hover:text-blue-600 truncate transition-colors no-underline flex-1 min-w-0"
          >
            {displayName}
          </Link>

          <div className="flex items-center gap-1.5 sm:gap-2.5 shrink-0">
            {/* Green countdown pill */}
            <div className="flex items-center gap-1 px-2 sm:px-2.5 py-0.5 rounded-full bg-[#ECFDF5] border border-[#A7F3D0] text-[#059669] font-grotesk text-[11px] sm:text-[12px] font-semibold">
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              <span>{quota.daysLeft} 天</span>
            </div>

            {/* Context menu ⋮ */}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onEdit(deck);
              }}
              aria-label={`編輯 ${deck.name}`}
              className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="5" r="1.5" />
                <circle cx="12" cy="12" r="1.5" />
                <circle cx="12" cy="19" r="1.5" />
              </svg>
            </button>
          </div>
        </div>

        {/* Subtitle */}
        <div className="text-[12px] sm:text-[13px] text-slate-500 mt-1 truncate">
          {deck.deadlineLabel || "DSE 2027 考期"} {dateLabel(deck.deadline!)} · 剩 {fmt(quota.remaining)} 詞 · 每天需 {quota.newPerDay} 個新詞
        </div>

        {/* Progress bar */}
        <div className="flex items-center gap-3 mt-2.5 sm:mt-3">
          <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full rounded-full bg-[#2563EB] transition-all duration-500"
              style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
            />
          </div>
          <span className="font-grotesk text-[11.5px] sm:text-[12.5px] text-slate-400 shrink-0">
            {fmt(learned)} / {fmt(total)}
          </span>
        </div>
      </div>
    </div>
  );
}

export default Today;
